import { useNavigate, useLocation } from "react-router-dom";
import { Badge, Tooltip } from "antd";
import { useUserStore } from "../../store/slices/user";
import { AppRoute } from "./routes";

interface SidebarMenuProps {
  collapsed?: boolean;
  routes?: AppRoute[];
}

const SidebarMenu = ({ collapsed = false, routes = [] }: SidebarMenuProps) => {
  const navigate = useNavigate();
  const location = useLocation();
  const { user, logout } = useUserStore();

  const isActive = (url?: string) => {
    if (!url) return false;

    // Home/Dashboard route
    if (url === "/") {
      return location.pathname === "/";
    }

    // Exact match
    if (location.pathname === url) {
      return true;
    }

    // Nested routes
    return location.pathname.startsWith(url + "/");
  };

  const groupedRoutes = routes
    .filter((item: any) => !item.hidden)
    .reduce((acc: any, item: any) => {
      const section = item.type || "MAIN";
      if (!acc[section]) acc[section] = [];
      acc[section].push(item);
      return acc;
    }, {});

  const renderMenuItem = (item: any) => {
    const active = isActive(item.url);
    const label = item.url ? item.label : "";

    const content = (
      <div className="nav-item-content flex w-full items-center gap-3">
        {/* ICON */}
        <span
          className={`${active ? "text-black" : "text-blue-400"} text-[16px] shrink-0`}
        >
          {item.icon && <item.icon />}
        </span>

        {!collapsed && (
          <>
            {/* LABEL */}
            <span className="text-sm truncate">{label}</span>

            {/* BADGE */}
            {item.count ? (
              <Badge
                count={item.count}
                size="small"
                style={{
                  background: item.badgeColor || "#3B82F6",
                  boxShadow: "none",
                  fontWeight: 600,
                  fontSize: 11,
                  minWidth: 20,
                  height: 20,
                  lineHeight: "20px",
                  borderRadius: "9999px",
                  padding: 0,
                }}
                className="ml-auto"
              />
            ) : null}
          </>
        )}
      </div>
    );

    if (collapsed) {
      return (
        <Tooltip key={item.key} title={label} placement="right">
          <div
            onClick={() => navigate(item.url)}
            className={`h-11 w-11 mx-auto rounded-lg flex items-center justify-center cursor-pointer ${
              active
                ? "active bg-white text-black"
                : "text-blue-200 hover:text-white"
            }`}
          >
            {item.icon && <item.icon className="w-4 h-4" />}
          </div>
        </Tooltip>
      );
    }

    return (
      <div
        key={item.key}
        onClick={() => navigate(item.url)}
        className={`nav-item flex items-center gap-3 px-3 py-2 mx-2 cursor-pointer text-sm transition-all duration-150 ${
          active
            ? "active bg-white text-black font-semibold rounded-lg"
            : "text-blue-200 hover:text-white"
        }`}
      >
        {content}
      </div>
    );
  };

  return (
    <aside
      className={`bg-[#000000] flex flex-col justify-between flex-shrink-0 h-screen z-50 ${
        collapsed ? "w-[88px]" : "w-[260px]"
      }`}
    >
      {/* LOGO */}
      {!collapsed && (
        <div className="sidebar-logo flex items-center justify-center px-5 py-4 mb-2">
          <div className="w-full h-12 flex items-center justify-center">
            <img
              src="/dark_logo.png"
              alt="ArtPark Logo"
              className="w-full h-full object-contain"
            />
          </div>
        </div>
      )}
      {collapsed && (
        <Tooltip key="FIELD_SERVICES" title="ArtPark" placement="right">
          <div className="sidebar-logo flex justify-center gap-3 px-5 py-4 mb-2">
            <div className="w-8 h-8 bg-white/20 rounded-lg flex items-center justify-center">
              <svg
                className="w-5 h-5 text-white"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth="2"
                  d="M13 10V3L4 14h7v7l9-11h-7z"
                />
              </svg>
            </div>
          </div>
        </Tooltip>
      )}

      {/* MENU */}
      <nav className="flex-1 px-2 space-y-0.5 overflow-y-auto custom-scrollbar">
        {Object.entries(groupedRoutes).map(([section, items]: any) => (
          <div key={section}>
            {!collapsed && (
              <div className="text-blue-500 text-xs font-semibold px-3 py-1 mt-1 uppercase tracking-wider">
                {section}
              </div>
            )}

            <div>{items.map((item: any) => renderMenuItem(item))}</div>
          </div>
        ))}
      </nav>
    </aside>
  );
};

export default SidebarMenu;
